<script setup lang="ts">
import type { testimonialContainerVariants } from '.';
import type { Testimonial } from '@/components/Testimonial';
import type Carousel from '@/components/ui/carousel/Carousel.vue';

import { Container } from '@/components/Container';
import { CarouselGenericAction } from '@/components/ui/carousel/CarouselGenericAction';
import { CarouselItemSelector } from '@/components/ui/carousel/CarouselItemSelector';
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures';

import { TestimonialCard } from '~/components/Testimonial';

import { testimonialContainer, testimonialHeader } from '.';

withDefaults(
	defineProps<{
		testimonials: Testimonial[];
		variant?: testimonialContainerVariants['variant'];
	}>(),
	{
		variant: 'dark'
	}
);

// Constants
const SLIDE_DELAY = 2000;

// Refs and States
const carouselContainerRef = ref<InstanceType<typeof Carousel> | null>(null);

const carouselItemSelectorContainerRef = ref<HTMLDivElement | null>(null);

const {
	currentCarouselItemIndex,
	scrollableSlidesNumber,
	carouselDynamicItemWidth,
	scrollTo
} = useCarouselControl({
	carouselContainerRef,
	carouselItemSelectorContainerRef,
	cyclic: true
});
</script>
<template>
	<section :class="testimonialContainer({ variant })">
		<Container class="px-0 py-[44px] sm:py-[60px]">
			<header class="flex flex-col items-start px-5 pb-9 md:px-0">
				<h2 :class="testimonialHeader({ variant })">
					O que dizem nossos clientes
				</h2>
			</header>

			<Carousel
				ref="carouselContainerRef"
				class="relative w-full"
				:plugins="[WheelGesturesPlugin()]"
				:opts="{ align: 'center' }"
			>
				<CarouselContent class="gap-5 pl-5 md:gap-11 md:pl-0">
					<CarouselItem
						v-for="testimonial in testimonials"
						:key="testimonial.author"
						class="max-h-[550px] max-w-[311px] md:max-h-[650px] md:max-w-[327px]"
					>
						<TestimonialCard
							:testimonial="testimonial"
							class="p-0"
							:variant="variant"
						/>
					</CarouselItem>
				</CarouselContent>
				<div
					class="mt-6 flex h-auto w-full items-center gap-5 sm:left-auto sm:mt-9 sm:flex-row sm:justify-end md:mx-0 2xl:hidden"
				>
					<div class="flex w-full items-center gap-5">
						<div
							ref="carouselItemSelectorContainerRef"
							class="flex w-full flex-wrap justify-end gap-3"
						>
							<CarouselItemSelector
								v-for="(_, index) in scrollableSlidesNumber"
								:key="`selector-${index}`"
								:active="currentCarouselItemIndex === index"
								:delay="SLIDE_DELAY"
								:dynamic-width="carouselDynamicItemWidth"
								@click="scrollTo(index)"
							/>
						</div>
						<div class="flex gap-1">
							<CarouselGenericAction
								@click="scrollTo(currentCarouselItemIndex - 1)"
							>
								<IconMaterialSymbolsLightChevronLeft
									width="28px"
									height="28px"
								/>
							</CarouselGenericAction>
							<CarouselGenericAction>
								<IconMaterialSymbolsLightChevronRight
									width="28px"
									height="28px"
									@click="scrollTo(currentCarouselItemIndex + 1)"
								/>
							</CarouselGenericAction>
						</div>
					</div>
				</div>
			</Carousel>
			<!-- Carousel Controls -->
		</Container>
	</section>
</template>
